import { Attrs, MinzeElement, Reactive } from 'minze'
import { LvcItemSourceData } from '../../../types/global'
import { LvcHomeActivityPackData } from '../../componentsV2'
import { Locale } from '../../modules/locale'
import MainModule from '../../modules/main'
import { ISearchConfig } from '../../modules/search'
import { LvcActivityPackSubscriber } from './lvc-activity-pack'
import { fetchPackItems, PrebuiltPacks } from './utils'

export interface LvcActivityPackSquidex {
  title: string
  titleHtmlTag: 'h2' | 'h3' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | null
  dataPackConfig?: string
  prebuiltTag?: PrebuiltPacks
  layout: 'grid' | 'slider'
  enableTitleOnDesktopView: boolean
  displayOnlyForUserType?: 'authenticated' | 'not-authenticated' | null
  loading: boolean
  instanceId: string
  groupId: string
  subscriber: LvcActivityPackSubscriber | null
}

export class LvcActivityPackSquidex extends MinzeElement {
  attrs: Attrs = [
    ['title', ''],
    ['title-html-tag', 'h2'],
    ['data-pack-config', ''],
    ['prebuilt-tag', ''],
    ['layout', 'grid'],
    ['enable-title-on-desktop-view', false],
    ['display-only-for-user-type', null],
  ]

  // Instance management properties
  instanceId: string
  groupId: string
  subscriber: LvcActivityPackSubscriber | null = null
  isDestroyed = false
  debounceTimeout: number | null = null

  loading = true
  activityPackData = {} as LvcHomeActivityPackData
  items = [] as LvcItemSourceData[]
  disableViewMore = false
  disableLimit = false
  userRadius: null | number = null
  searchConfig = {} as ISearchConfig

  constructor() {
    super()
    this.instanceId = this.generateInstanceId()
    this.groupId = this.generateGroupId(this.instanceId)
  }

  /**
   * Generate a unique instance ID using crypto.randomUUID() with fallback
   */
  private generateInstanceId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    // Fallback for environments without crypto.randomUUID
    return (
      'activity-pack-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36)
    )
  }

  /**
   * Generate a unique group ID for the search core
   */
  private generateGroupId(instanceId: string): string {
    return `activity-pack-${instanceId}`
  }

  /**
   * Clean up subscriber and search core resources
   */
  private cleanupSubscriber(): void {
    if (this.subscriber) {
      try {
        MainModule.getInstance().getSearchManager().unsubscribe(this.subscriber)
      } catch (error) {
        console.warn('Error unsubscribing from search manager:', error)
      }
      this.subscriber = null
    }
  }
  onStart = () => {
    // Create instance-specific subscriber with unique group ID
    this.subscriber = new LvcActivityPackSubscriber(this.groupId)
    MainModule.getInstance().getSearchManager().subscribe(this.subscriber)

    MainModule.getInstance().getEE().on('geo:location', this.handleStateChange)
    MainModule.getInstance().getEE().on('lvc:user_state_changed', this.handleStateChange)
  }

  onDestroy = () => {
    this.isDestroyed = true

    // Clear any pending debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = null
    }

    // Clean up subscriber and search core
    this.cleanupSubscriber()

    // Remove event listeners
    MainModule.getInstance().getEE().off('geo:location', this.handleStateChange)
    MainModule.getInstance().getEE().off('lvc:user_state_changed', this.handleStateChange)
  }
  fetchData = async (location: { latitude: number; longitude: number }) => {
    // Instance validation - prevent cross-component interference
    if (this.isDestroyed || !this.subscriber) {
      console.warn('Component destroyed or subscriber not available, skipping fetchData')
      return
    }

    const dataPackConfig = this?.dataPackConfig
    const prebuiltTag = this?.prebuiltTag as PrebuiltPacks
    this.activityPackData = {
      title: {
        fr:
          JSON.parse(decodeURIComponent(this?.dataPackConfig || '')).title.fr ||
          JSON.parse(decodeURIComponent(this.title))[Locale.language as 'fr' | 'en'] ||
          "Pack d'activités",
        en:
          JSON.parse(decodeURIComponent(this?.dataPackConfig || '')).title.en ||
          JSON.parse(decodeURIComponent(this.title)) ||
          'Activity Pack',
      },
      dataPackConfig,
      prebuiltTag,
    }
    // decode the dataPackConfig so I can play with it hehehe
    const decodedDataPackConfig = JSON.parse(
      decodeURIComponent(this.activityPackData?.dataPackConfig || '')
    )

    // if the dataPackConfig does not have a radius and the user has a preferred radius, set it
    if (decodedDataPackConfig && !decodedDataPackConfig?.radius && this.userRadius) {
      decodedDataPackConfig.radius = this.userRadius
    }

    // Use the instance-specific subscriber instead of creating a new one
    const packItems = await fetchPackItems(
      this.subscriber,
      decodedDataPackConfig,
      this.activityPackData?.prebuiltTag,
      location
    )

    // Final validation before setting results
    if (this.isDestroyed) {
      console.warn('Component destroyed during fetchData, discarding results')
      return
    }

    this.searchConfig = packItems?.searchConfig || {}
    this.items = packItems?.items ?? []
    this.total = packItems?.total || 0
    this.disableViewMore = packItems?.disableViewMore || false
    this.disableLimit = packItems?.disableLimit || false
    this.limit = decodedDataPackConfig?.limit || 14
  }

  handleStateChange = async () => {
    // Instance validation
    if (this.isDestroyed) {
      return
    }

    // Clear any existing debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    // Debounce rapid successive calls
    this.debounceTimeout = window.setTimeout(async () => {
      // Double-check instance is still valid after timeout
      if (this.isDestroyed) {
        return
      }

      const isLoggedIn = MainModule.getInstance().getAuth().isLoggedIn()
      const location = MainModule.getInstance().getGeo().currentLocation

      // get the user's preferred radius from the user's profile
      const userData = MainModule.getInstance().getAuth().getUserData()

      if (userData?.profile?.personalData?.radius) {
        this.userRadius = userData.profile.personalData.radius
      }

      this.loading = true
      if (
        !isLoggedIn &&
        (this.prebuiltTag === 'recommended' || this.displayOnlyForUserType === 'authenticated')
      ) {
        this.items = []
        this.loading = false
        return this.rerender()
      }
      if (isLoggedIn && this.displayOnlyForUserType === 'not-authenticated') {
        this.items = []
        this.loading = false
        return this.rerender()
      }
      await this.fetchData(location)
      this.loading = false
      this.rerender()
    }, 100) // 100ms debounce delay
  }
  html = () => {
    if (this.loading) {
      return /*html*/ `
        <div class='loader-wrapper'>
          <div class='loader'></div>
        </div>
      `
    }
    return `
    ${
      this.items.length > 0
        ? /*html*/ `
      <lvc-activity-pack-layout
          title="${this.activityPackData?.title?.[Locale.language as 'fr' | 'en'] || ''}"
          title-html-tag="${this.titleHtmlTag || 'h2'}"
          items="${encodeURIComponent(JSON.stringify(this.items))}"
          total="${this.total || 0}"
          search-config="${encodeURIComponent(JSON.stringify(this.searchConfig))}"
          disable-view-more="${this.disableViewMore}"
          disable-limit="${this.disableLimit}"
          enable-title-on-desktop-view=${this.enableTitleOnDesktopView}
          layout="${this.layout}"
          limit="${this.limit}"
        >
        </lvc-activity-pack-layout>
      `
        : ''
    }
    `
  }
  css = () => `
    .loader-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 400px;
    }
    .loader {
      width: 48px;
      height: 48px;
      border: 5px solid #1E007D25;
      border-bottom-color: transparent;
      border-radius: 50%;
      display: inline-block;
      box-sizing: border-box;
      animation: rotation 1s linear infinite;
    }

    @keyframes rotation {
      0% {
          transform: rotate(0deg);
      }
      100% {
          transform: rotate(360deg);
      }
    }
  `
}
